import type { ShopSeoData } from './seo'

type CustomerRouteMode = 'signup' | 'login' | 'activate' | 'forgot-password' | 'reset-password' | 'account' | null

export const buildAppEffectiveSeo = ({
  language,
  runtimeSiteName,
  customerRouteMode,
  isCartRoute,
  hasRuntimeSeoData,
  seo,
  initialSeo,
}: {
  language: 'it' | 'en'
  runtimeSiteName: string
  customerRouteMode: CustomerRouteMode
  isCartRoute: boolean
  hasRuntimeSeoData: boolean
  seo: ShopSeoData
  initialSeo?: ShopSeoData
}) => {
  const cartSeo = isCartRoute
    ? {
        title: `${language === 'en' ? 'Cart' : 'Carrello'} | ${runtimeSiteName}`,
        description:
          language === 'en'
            ? 'Manage products in your cart before checkout.'
            : 'Gestisci i prodotti aggiunti al carrello prima del checkout.',
        canonicalPath: '/cart',
        ogType: 'website' as const,
        pageType: 'page' as const,
        entityName: language === 'en' ? 'Cart' : 'Carrello',
        breadcrumbs: [
          { name: 'Home', path: '/' },
          { name: language === 'en' ? 'Cart' : 'Carrello', path: '/cart' },
        ],
      }
    : null

  const customerSeo = customerRouteMode
    ? {
        title:
          customerRouteMode === 'signup'
            ? `${language === 'en' ? 'Customer Sign Up' : 'Registrazione Cliente'} | ${runtimeSiteName}`
            : customerRouteMode === 'login'
            ? `${language === 'en' ? 'Customer Login' : 'Login Cliente'} | ${runtimeSiteName}`
            : customerRouteMode === 'activate'
            ? `${language === 'en' ? 'Activate Account' : 'Attiva Account'} | ${runtimeSiteName}`
            : customerRouteMode === 'forgot-password'
            ? `${language === 'en' ? 'Recover Password' : 'Recupera Password'} | ${runtimeSiteName}`
            : customerRouteMode === 'reset-password'
            ? `${language === 'en' ? 'Reset Password' : 'Reimposta Password'} | ${runtimeSiteName}`
            : `${language === 'en' ? 'Customer Area' : 'Area Cliente'} | ${runtimeSiteName}`,
        description:
          customerRouteMode === 'account'
            ? language === 'en'
              ? 'Manage profile, orders and support requests in your customer area.'
              : 'Gestisci profilo, ordini e richieste assistenza dalla tua area cliente.'
            : language === 'en'
            ? 'Sign in or sign up to use customer area.'
            : 'Accedi o registrati per usare l area cliente.',
        canonicalPath:
          customerRouteMode === 'signup'
            ? '/dashboard/signup'
            : customerRouteMode === 'login'
            ? '/dashboard/login'
            : customerRouteMode === 'activate'
            ? '/account/activate'
            : customerRouteMode === 'forgot-password'
            ? '/dashboard/forgot-password'
            : customerRouteMode === 'reset-password'
            ? '/dashboard/reset-password'
            : '/dashboard',
        ogType: 'website' as const,
        pageType: 'page' as const,
        entityName: language === 'en' ? 'Customer Area' : 'Area Cliente',
        breadcrumbs: [
          { name: 'Home', path: '/' },
          { name: language === 'en' ? 'Customer Area' : 'Area Cliente', path: '/dashboard' },
        ],
      }
    : null

  return customerSeo ?? cartSeo ?? (hasRuntimeSeoData ? seo : initialSeo || seo)
}
